import {
  type ColumnDef,
  type ColumnVisibilityState,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Columns3, ExternalLink, RotateCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { type DataTableFeatures, RemoteDataTable } from "~/components/data-table";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui";

import { useDbpjkitWorkerJobList } from "~/features/dbpjkitWorkerJobs/hooks";
import type { JobListFilters, JobRaw, JobSortOrder, SortDirection } from "~/features/dbpjkitWorkerJobs/types";
import { listSpreadsheetUrl, statusTone } from "~/features/dbpjkitWorkerJobs/utils/transformers";

const SORTABLE: ReadonlySet<string> = new Set([
  "commandName",
  "googleSpreadsheetId",
  "googleSheetId",
  "createdAt",
  "updatedAt",
]);

const JOB_COLUMNS = [
  { id: "commandName", labelKey: "model.dbpjkitWorkerJob.commandName" },
  { id: "googleSpreadsheetId", labelKey: "model.dbpjkitWorkerJob.googleSpreadsheetID" },
  { id: "googleSheetId", labelKey: "model.dbpjkitWorkerJob.googleSheetID" },
  { id: "createdAt", labelKey: "model.common.createdAt" },
  { id: "updatedAt", labelKey: "model.common.updatedAt" },
  { id: "latestRun", labelKey: "ui.latestRun" },
] as const;

interface DbpjkitWorkerJobsTableProps {
  filters: JobListFilters;
  sortOrder: JobSortOrder;
  direction: SortDirection;
  onSortingChange: (order: JobSortOrder, direction: SortDirection) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  onSelect: (id: string) => void;
}

const helper = createColumnHelper<DataTableFeatures, JobRaw>();

export function DbpjkitWorkerJobsTable({
  filters,
  sortOrder,
  direction,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  onSelect,
}: DbpjkitWorkerJobsTableProps) {
  const { t } = useTranslation();
  const { data, refetch } = useDbpjkitWorkerJobList({ filters, sortOrder, direction, pageIndex, pageSize });
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({});

  const rows = data.dbpjkitWorkerJobs;
  const totalCount = data.dbpjkitWorkerJobsAggregate.aggregate?.count ?? 0;

  const isVisible = (id: string) => columnVisibility[id] !== false;
  const setVisibility = (id: string, next: boolean) => setColumnVisibility((prev) => ({ ...prev, [id]: next }));
  const toggleAll = (next: boolean) => setColumnVisibility(Object.fromEntries(JOB_COLUMNS.map((c) => [c.id, next])));

  const sorting: SortingState = useMemo(() => [{ id: sortOrder, desc: direction === "desc" }], [sortOrder, direction]);

  const handleSortingChange = (next: SortingState) => {
    const first = next[0];
    if (!first || !SORTABLE.has(first.id)) return;
    onSortingChange(first.id as JobSortOrder, first.desc ? "desc" : "asc");
  };

  const columns: ColumnDef<DataTableFeatures, JobRaw, any>[] = [
    helper.accessor("commandName", {
      id: "commandName",
      header: () => t("model.dbpjkitWorkerJob.commandName"),
      cell: (info) => <span className="font-medium text-fg-1">{info.getValue() || "—"}</span>,
    }),
    helper.accessor("googleSpreadsheetId", {
      id: "googleSpreadsheetId",
      header: () => t("model.dbpjkitWorkerJob.googleSpreadsheetID"),
      cell: (info) => {
        const row = info.row.original;
        return (
          <span className="inline-flex items-center gap-1.5 text-fg-2">
            <span className="tabular-nums">{row.googleSpreadsheetId}</span>
            <a
              href={listSpreadsheetUrl(row.googleSpreadsheetId, row.googleSheetId)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-fg-link hover:text-fg-1"
              aria-label={t("model.dbpjkitWorkerJob.googleSpreadsheetID")}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </span>
        );
      },
    }),
    helper.accessor("googleSheetId", {
      id: "googleSheetId",
      header: () => t("model.dbpjkitWorkerJob.googleSheetID"),
      cell: (info) => <span className="tabular-nums text-fg-2">{info.getValue()}</span>,
    }),
    helper.accessor("createdAt", {
      id: "createdAt",
      header: () => t("model.common.createdAt"),
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="whitespace-nowrap tabular-nums text-fg-2">
            {value ? t("date.full", { date: parseISO(value) }) : "—"}
          </span>
        );
      },
    }),
    helper.accessor("updatedAt", {
      id: "updatedAt",
      header: () => t("model.common.updatedAt"),
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="whitespace-nowrap tabular-nums text-fg-2">
            {value ? t("date.full", { date: parseISO(value) }) : "—"}
          </span>
        );
      },
    }),
    helper.display({
      id: "latestRun",
      header: () => t("ui.latestRun"),
      enableSorting: false,
      cell: (info) => {
        const latestRun = info.row.original.dbpjkitWorkerJobRuns[0];
        if (!latestRun) return <span className="text-fg-3">—</span>;
        return (
          <span className="inline-flex items-center gap-2">
            <Badge tone={statusTone(latestRun.statusId)}>{latestRun.statusId}</Badge>
            <span className="whitespace-nowrap tabular-nums text-fg-2">
              {latestRun.queuedAt ? t("date.full", { date: parseISO(latestRun.queuedAt) }) : "—"}
            </span>
          </span>
        );
      },
    }),
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="icon" aria-label={t("ui.columns")}>
              <Columns3 />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuItem onSelect={() => toggleAll(false)}>{t("ui.hideAll")}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleAll(true)}>{t("ui.showAll")}</DropdownMenuItem>
            <DropdownMenuSeparator />
            {JOB_COLUMNS.map((col) => (
              <DropdownMenuCheckboxItem
                key={col.id}
                checked={isVisible(col.id)}
                onCheckedChange={(checked) => setVisibility(col.id, checked === true)}
                onSelect={(e) => e.preventDefault()}
              >
                {t(col.labelKey)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button type="button" variant="outline" size="icon" onClick={() => void refetch()} aria-label={t("ui.reload")}>
          <RotateCw />
        </Button>
      </div>

      <RemoteDataTable<JobRaw>
        columns={columns}
        data={rows}
        rowCount={totalCount}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        isPending={isPending}
        onRowClick={(row) => onSelect(row.id)}
        emptyMessage={t("feature.common.noData")}
      />
    </div>
  );
}
